/**
 * Legacy Pricing Methods — Computes results from traditional pricing approaches.
 *
 * Three methods:
 * 1. Square Inch Pricing — PSI × area
 * 2. Hourly + Materials — (hours × hourlyRate) + materials
 * 3. Cost Multiplier — materials × 3 (standard gallery markup)
 *
 * Used by PricePositionArc to show the "legacy range" band.
 * All inputs and outputs in DOLLARS (not cents).
 */

import { PSI_BASE } from '@/constants/psi';
import { interpolateSizeMultiplier } from '@/constants/size';
import type { MediumKey } from '@/types/medium';

export interface LegacyMethod {
  name: string;
  description: string;
  price: number;   // dollars
}

export interface LegacyRange {
  methods: LegacyMethod[];
  low: number;     // dollars — min of all methods
  high: number;    // dollars — max of all methods
}

export function computeLegacyRange(inputs: {
  medium: MediumKey;
  width: number;      // inches
  height: number;     // inches
  hours: number;
  materials: number;  // cents
  monthlyBurn: number;  // cents
  monthlyCapacity: number; // hours
}): LegacyRange {
  const area = inputs.width * inputs.height;
  const materialsDollars = Math.round(inputs.materials / 100);
  const hourlyRate = inputs.monthlyCapacity > 0
    ? Math.round(inputs.monthlyBurn / inputs.monthlyCapacity / 100)
    : 25; // fallback $25/hr

  const psiRate = PSI_BASE[inputs.medium] ?? 1.00;

  const sizeMultiplier = interpolateSizeMultiplier(area);

  const methods: LegacyMethod[] = [
    {
      name: 'Square Inch Pricing',
      description: `${area} sq in × $${psiRate.toFixed(2)}/sq in × ${sizeMultiplier.toFixed(2)}`,
      price: Math.round(area * psiRate * sizeMultiplier),
    },
    {
      name: 'Hourly + Materials',
      description: `${inputs.hours}h × $${hourlyRate}/hr + $${materialsDollars} materials`,
      price: Math.round(inputs.hours * hourlyRate + materialsDollars),
    },
    {
      name: 'Cost Multiplier',
      description: `$${materialsDollars} materials × 3`,
      price: Math.round(materialsDollars * 3),
    },
  ];

  // Sort ascending
  methods.sort((a, b) => a.price - b.price);

  const prices = methods.map(m => m.price);
  const low = Math.min(...prices);
  const high = Math.max(...prices);

  return { methods, low, high };
}
